/**
 * index.js – Unified entry point
 * Drives ALL keys in SAT_CONFIG without hard-coding any names here.
 */

import { SAT_CONFIG, loadAllTLEs, clearTLECache } from "./satellites.js";
import {
  initMap2D, destroyMap2D,
  updateSat2D, drawGroundTrack2D,
  updateConstellation2D,
  focusSat2D,
  buildConstellationPanel,
  refreshConstellationDots,
} from "./map2d.js";
import {
  initScene3D, destroyScene3D,
  updateSat3D, buildOrbitLine3D,
  updateConstellationCloud3D,
  focusSat3D,
  set3DAutoRotate, set3DShowTracks, set3DShowAtmo, set3DShowStars,
} from "./scene3d.js";

// ── State ─────────────────────────────────────────────────
let tleData = null;
let mode    = "2d";

// ── DOM helper ────────────────────────────────────────────
const $ = (id) => document.getElementById(id);
function setStatus(msg) { $("status-msg").textContent = msg; }

function updateSidebarCard(key, pos) {
  if (!pos) return;
  ["lat","lon","alt","vel"].forEach((f) => {
    const el = $(`${key}-${f}`);
    if (!el) return;
    if (f === "lat") el.textContent = pos.lat.toFixed(2) + "°";
    else if (f === "lon") el.textContent = pos.lon.toFixed(2) + "°";
    else if (f === "alt") el.textContent = pos.alt.toFixed(0);
    else if (f === "vel") el.textContent = pos.velocity.toFixed(2);
  });
}

// ── UTC clock ─────────────────────────────────────────────
function startClock() {
  setInterval(() => {
    const el = $("utc-clock");
    if (el) el.textContent = "UTC " + new Date().toUTCString().slice(17, 25);
  }, 1000);
}

// ── Tick helpers ──────────────────────────────────────────
function tick2D() {
  if (!tleData || mode !== "2d") return;
  Object.keys(SAT_CONFIG).forEach((key) => {
    const cfg  = SAT_CONFIG[key];
    const data = tleData[key];
    if (!data?.length) return;
    if (cfg.type === "single") {
      updateSidebarCard(key, updateSat2D(key, data[0]));
    } else {
      updateConstellation2D(key, data);
    }
  });
  refreshConstellationDots(tleData);
}

function tick3D() {
  if (!tleData || mode !== "3d") return;
  Object.keys(SAT_CONFIG).forEach((key) => {
    const cfg  = SAT_CONFIG[key];
    const data = tleData[key];
    if (!data?.length) return;
    if (cfg.type === "single") {
      updateSidebarCard(key, updateSat3D(key, data[0]));
    } else {
      updateConstellationCloud3D(key, data);
    }
  });
}

function tick() {
  if (mode === "2d") tick2D(); else tick3D();
}

// ── 2D boot ───────────────────────────────────────────────
function boot2D() {
  initMap2D("map2d");
  if (!tleData) return;
  Object.keys(SAT_CONFIG).forEach((key) => {
    const cfg  = SAT_CONFIG[key];
    const data = tleData[key];
    if (!data?.length) return;
    if (cfg.type === "single") drawGroundTrack2D(key, data[0]);
    else updateConstellation2D(key, data);
  });
  tick2D();
}

// ── 3D boot ───────────────────────────────────────────────
// Only initialises the Three.js scene. Data is injected by
// fetchAndInit() once TLEs are available (avoids race with async fetch).
function boot3D() {
  initScene3D("canvas3d");
  // If TLE data is already loaded (e.g. switched from 2D), wire it now.
  if (tleData) {
    Object.keys(SAT_CONFIG).forEach((key) => {
      const cfg  = SAT_CONFIG[key];
      const data = tleData[key];
      if (!data?.length) return;
      if (cfg.type === "single") buildOrbitLine3D(key, data[0], 92);
      else updateConstellationCloud3D(key, data);
    });
    tick3D();
  }
}

// ── TLE fetch + wiring ────────────────────────────────────
async function fetchAndInit() {
  setStatus("Fetching TLE data…");
  try {
    tleData = await loadAllTLEs();

    if (mode === "2d") {
      buildConstellationPanel(tleData);
      Object.keys(SAT_CONFIG).forEach((key) => {
        const cfg  = SAT_CONFIG[key];
        const data = tleData[key];
        if (!data?.length) return;
        if (cfg.type === "single") drawGroundTrack2D(key, data[0]);
        else updateConstellation2D(key, data);
      });
    } else {
      Object.keys(SAT_CONFIG).forEach((key) => {
        const cfg  = SAT_CONFIG[key];
        const data = tleData[key];
        if (!data?.length) return;
        if (cfg.type === "single") buildOrbitLine3D(key, data[0], 92);
        else updateConstellationCloud3D(key, data);
      });
    }

    setStatus("Live · updates every 5 s");
    tick();
  } catch (err) {
    console.error(err);
    setStatus("TLE fetch failed – retrying in 60 s");
    setTimeout(fetchAndInit, 60_000);
  }
}

// ── Mode switch ───────────────────────────────────────────
function switchTo(newMode) {
  if (newMode === mode) return;
  mode = newMode;

  const wrap2d  = $("wrap-2d");
  const wrap3d  = $("wrap-3d");
  const ctrl3d  = $("controls-3d");
  const constSec = $("constellation-section");

  if (mode === "3d") {
    destroyMap2D();
    wrap2d.style.display = "none";
    wrap3d.style.display = "block";
    $("btn-3d").classList.add("active");
    $("btn-2d").classList.remove("active");
    if (ctrl3d)   ctrl3d.style.display   = "block";
    if (constSec) constSec.style.display = "none";
    boot3D();
  } else {
    destroyScene3D();
    wrap3d.style.display = "none";
    wrap2d.style.display = "block";
    $("btn-2d").classList.add("active");
    $("btn-3d").classList.remove("active");
    if (ctrl3d)   ctrl3d.style.display   = "none";
    if (constSec) constSec.style.display = "";
    clearTLECache();
    fetchAndInit().then(() => boot2D());
  }
}

// ── 3D toggles ────────────────────────────────────────────
function setup3DToggles() {
  const defs = [
    ["btn-tracks", true,  set3DShowTracks],
    ["btn-rotate", true,  set3DAutoRotate],
    ["btn-atmo",   true,  set3DShowAtmo],
    ["btn-stars",  true,  set3DShowStars],
  ];
  defs.forEach(([id, init, cb]) => {
    let state = init;
    const btn = $(id);
    if (!btn) return;
    btn.addEventListener("click", () => {
      state = !state;
      btn.classList.toggle("on", state);
      btn.textContent = state ? "ON" : "OFF";
      cb(state);
    });
  });
}

// ── Card clicks (singles only) ────────────────────────────
function bindCardClicks() {
  Object.keys(SAT_CONFIG).forEach((key) => {
    if (SAT_CONFIG[key].type !== "single") return;
    const card = $(`card-${key}`);
    if (!card) return;
    card.addEventListener("click", () => {
      if (mode === "2d") focusSat2D(key); else focusSat3D(key);
      Object.keys(SAT_CONFIG).forEach((k) => {
        if (SAT_CONFIG[k].type !== "single") return;
        $(`card-${k}`)?.classList.toggle("active", k === key);
      });
    });
  });
}

// ── Boot ──────────────────────────────────────────────────
document.addEventListener("DOMContentLoaded", () => {
  startClock();
  setup3DToggles();
  bindCardClicks();

  $("btn-2d").addEventListener("click", () => switchTo("2d"));
  $("btn-3d").addEventListener("click", () => switchTo("3d"));

  boot2D();
  fetchAndInit();

  setInterval(tick,          5_000);
  setInterval(() => { clearTLECache(); fetchAndInit(); }, 30 * 60_000);
});